// Backend · Alerts: the Triggered tab (active and resolved incidents, grouped
// by production or category, ack/resolve) and the email-linked incident panel.

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ResponsiveKpis } from '@/components/responsive-kpis';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { Dropdown } from '@/components/dropdowns';
import { QueryErrorNote } from '@/components/error-panel';
import { adminAlertById, adminAlerts, adminAlertsResolvedPage } from '@/api/resources';
import { api } from '@/api/client';
import { ApiError } from '@/api/errors';
import { useRegisteredMutation } from '@/api/mutation';
import { useAlertById, usePixels, useProductions } from '@/api/hooks';
import type { Alert, AlertStatus, AlertStatusCounts } from '@/api/types';
import { queryClient } from '@/cache/queryClient';
import { useCan } from '@/auth/capabilities';
import { usePhone } from '@/lib/responsive';
import { CAT_LABEL } from '@/lib/alert-labels';
import { AlertCard, alertLogsHref, type AlertNameOf } from './backend-alerts-alert-card';
import { invalidate } from './backend-alerts-parts';

type StatusFilter = AlertStatus | 'all';
type GroupBy = 'none' | 'production' | 'category';

/** What the tab reads from a query: its state and a retry. */
export interface AlertsQueryState {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
}

interface OlderAlerts {
  alerts: Alert[];
  /** `undefined` until a page is read; then the next page's cursor, or `null` at the end. */
  cursor: string | null | undefined;
  loading: boolean;
  error: unknown;
}

const NO_OLDER_ALERTS: OlderAlerts = { alerts: [], cursor: undefined, loading: false, error: null };

export interface TriggeredTabProps {
  alerts: readonly Alert[];
  statusCounts?: AlertStatusCounts | null;
  resolvedCursor: string | null;
  alertsQuery: AlertsQueryState;
  incidentId: string | null;
}

export function TriggeredTab({ alerts: listed, statusCounts, resolvedCursor, alertsQuery, incidentId }: TriggeredTabProps) {
  const [status, setStatus] = useState<StatusFilter>('active');
  // Older resolved history, read on request. The list response carries every
  // unresolved alert and the newest page of resolved ones; each "Load older"
  // appends the next page. Nothing past what is shown is ever implied.
  const [older, setOlder] = useState<OlderAlerts>(NO_OLDER_ALERTS);
  // Older pages hang off the list's own boundary. When a refetch moves it (a
  // new resolve pushes a row out of the newest page) or the resolved total
  // changes, the loaded pages no longer join up, so they are dropped rather
  // than left with a gap; an in-flight page for the old boundary is ignored.
  const boundary = `${resolvedCursor ?? ''}|${statusCounts?.resolved ?? ''}`;
  const boundaryRef = useRef(boundary);
  useEffect(() => {
    boundaryRef.current = boundary;
    setOlder(NO_OLDER_ALERTS);
  }, [boundary]);
  const nextCursor = older.cursor === undefined ? resolvedCursor : older.cursor;
  const loadOlder = async () => {
    if (!nextCursor || older.loading) return;
    const asked = boundaryRef.current;
    setOlder((o) => ({ ...o, loading: true, error: null }));
    try {
      const page = await queryClient.fetchQuery(adminAlertsResolvedPage(nextCursor));
      if (boundaryRef.current !== asked) return;
      setOlder((o) => ({ alerts: [...o.alerts, ...page.alerts], cursor: page.resolved_cursor ?? null, loading: false, error: null }));
    } catch (error) {
      if (boundaryRef.current !== asked) return;
      setOlder((o) => ({ ...o, loading: false, error }));
    }
  };
  const alerts = useMemo(() => {
    const seen = new Set(listed.map((a) => a.id));
    return [...listed, ...older.alerts.filter((a) => !seen.has(a.id))];
  }, [listed, older.alerts]);
  const [category, setCategory] = useState<string>('all');
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const linkedQuery = useAlertById(incidentId);
  const linkedAlert = incidentId ? linkedQuery.data : null;
  const canLogs = useCan('logs_view', 'full');

  // id→name maps so incidents show a human name, not the raw id.
  const { data: productions = [] } = useProductions();
  const { data: pixels = [] } = usePixels();
  const prodName = useMemo(() => Object.fromEntries(productions.map((p) => [p.id, p.name])), [productions]);
  const pixelName = useMemo(() => Object.fromEntries(pixels.map((p) => [p.id, p.description])), [pixels]);
  const nameOf: AlertNameOf = (kind, id) => (kind === 'pixel' ? pixelName : prodName)[id] ?? id;

  const ackM = useRegisteredMutation({
    volatileResources: ['admin.alerts', 'admin.alertById'],
    mutationFn: (id: string) => api.post(`/admin/alerts/${encodeURIComponent(id)}/ack`, { body: {} }),
    onSuccess: () => invalidate(adminAlerts.all, adminAlertById.all),
  });
  const resolveM = useRegisteredMutation({
    volatileResources: ['admin.alerts', 'admin.alertById'],
    mutationFn: (id: string) => api.post(`/admin/alerts/${encodeURIComponent(id)}/resolve`, { body: {} }),
    onSuccess: () => invalidate(adminAlerts.all, adminAlertById.all),
  });

  const filtered = useMemo(() => {
    let out = alerts;
    // The linked incident has a dedicated, filter-independent location. Avoid
    // rendering it twice when it also happens to be in the list payload.
    if (linkedAlert) out = out.filter((a) => a.id !== linkedAlert.id);
    if (status !== 'all') out = out.filter((a) => a.status === status);
    if (category !== 'all') out = out.filter((a) => (a.category ?? '') === category);
    return out;
  }, [alerts, linkedAlert, status, category]);

  const groups = useMemo<Array<[string, Alert[]]>>(() => {
    if (groupBy === 'none') return [['', filtered]];
    const m = new Map<string, Alert[]>();
    for (const a of filtered) {
      // Group-by-production keys on the NAME (fallback id) so headers are human.
      const key = groupBy === 'production'
        ? (a.production_id ? nameOf('production', a.production_id) : '—')
        : (a.category ?? 'other');
      if (!m.has(key)) m.set(key, []);
      m.get(key)?.push(a);
    }
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [filtered, groupBy, prodName]);

  // Exact counts over the whole table, from the server: the list is not the
  // table (it held 200 of 307 when this was counted in the browser).
  const counts = useMemo<Record<AlertStatus, number>>(() => {
    if (statusCounts) return statusCounts;
    const c: Record<AlertStatus, number> = { active: 0, acked: 0, resolved: 0 };
    alerts.forEach((a) => { if (c[a.status] !== undefined) c[a.status]++; });
    return c;
  }, [statusCounts, alerts]);
  const resolvedShown = alerts.filter((a) => a.status === 'resolved').length;
  const historyCut = (status === 'resolved' || status === 'all') && !!nextCursor;

  const phone = usePhone();
  const alertFilters = (
    <ResponsiveFilters className="alert-kpi-filters" style={{ flex: 1, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', padding: '0 16px' }}>
      <Dropdown label="STATUS" data-filter-default="active" value={status} options={[{ id: 'active', label: 'Active' }, { id: 'acked', label: 'Acked' }, { id: 'resolved', label: 'Resolved' }, { id: 'all', label: 'All' }]} onChange={setStatus} width={130} />
      <Dropdown label="CATEGORY" value={category} options={[{ id: 'all', label: 'All' }, ...Object.entries(CAT_LABEL).map(([id, label]) => ({ id, label }))]} onChange={setCategory} width={160} />
      <Dropdown label="GROUP" value={groupBy} options={[{ id: 'none', label: 'No grouping' }, { id: 'production', label: 'By production' }, { id: 'category', label: 'By category' }]} onChange={setGroupBy} width={150} rightAlign />
    </ResponsiveFilters>
  );
  return (
    <>
      {incidentId && (
        <LinkedIncidentPanel
          incidentId={incidentId}
          query={linkedQuery}
          nameOf={nameOf}
          onAck={() => linkedAlert && ackM.mutate(linkedAlert.id)}
          onResolve={() => linkedAlert && resolveM.mutate(linkedAlert.id)}
          busy={ackM.isPending || resolveM.isPending}
          canLogs={canLogs}
        />
      )}

      {alertsQuery.isLoading ? (
        <div className="muted" role="status" aria-live="polite" style={{ padding: 40, textAlign: 'center', fontSize: 12 }}>
          Loading alerts…
        </div>
      ) : alertsQuery.isError ? (
        <QueryErrorNote error={alertsQuery.error} what="alerts" onRetry={() => void alertsQuery.refetch()} />
      ) : (
        <>
          {/* Desktop keeps the filters inside the strip, right-aligned; a phone strip scrolls sideways, so there they follow it. */}
          <ResponsiveKpis><div className="kpi-strip alert-kpi-strip" style={{ display: 'flex' }}>
            {!phone && alertFilters}
            <Kpi label="Active" value={counts.active} color="var(--bad)" />
            <Kpi label="Acked" value={counts.acked} color="var(--warn)" />
            <Kpi label="Resolved" value={counts.resolved} color="var(--good)" />

          </div></ResponsiveKpis>
          {phone && alertFilters}

          <div className="alert-card-list" style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'auto' }}>
            {filtered.length === 0 ? (
              <div className="muted" style={{ padding: '40px 12px', fontSize: 13, fontStyle: 'italic', textAlign: 'center' }}>
                {status === 'active'
                  ? linkedAlert?.status === 'active'
                    ? 'No other active alerts.'
                    : 'No active alerts. All clear. ✓'
                  : 'No alerts match.'}
              </div>
            ) : (
              groups.map(([gkey, list]) => (
                <div key={gkey || 'all'}>
                  {gkey !== '' && (
                    <div className="field-lbl mb-2" style={{ marginTop: 4 }}>{gkey} <span className="muted" style={{ textTransform: 'none', letterSpacing: 0 }}>· {list.length}</span></div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {list.map((a) => (
                      <AlertCard key={a.id} a={a} nameOf={nameOf} onAck={() => ackM.mutate(a.id)} onResolve={() => resolveM.mutate(a.id)} busy={ackM.isPending || resolveM.isPending} />
                    ))}
                  </div>
                </div>
              ))
            )}
            {historyCut && (
              <div className="muted" role="status" style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', padding: '8px 12px', fontSize: 12 }}>
                <span>Showing the newest {resolvedShown.toLocaleString()} of {counts.resolved.toLocaleString()} resolved incidents.</span>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => { void loadOlder(); }} disabled={older.loading}>
                  {older.loading ? 'Loading…' : 'Load older'}
                </button>
                {older.error ? <span style={{ color: 'var(--bad)' }}>Could not load older incidents.</span> : null}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

/** What the panel reads from the linked-incident query. */
export interface LinkedIncidentQuery extends AlertsQueryState {
  data: Alert | undefined;
  isPending: boolean;
}

export interface LinkedIncidentPanelProps {
  incidentId: string;
  query: LinkedIncidentQuery;
  nameOf: AlertNameOf;
  onAck: () => void;
  onResolve: () => void;
  busy: boolean;
  canLogs: boolean;
}

function LinkedIncidentPanel({ incidentId, query, nameOf, onAck, onResolve, busy, canLogs }: LinkedIncidentPanelProps) {
  const headingId = useId();
  const alert = query.data;
  const notFound = query.error instanceof ApiError && query.error.status === 404;

  return (
    <section className="alert-linked-incident" aria-labelledby={headingId} aria-busy={!alert && query.isLoading} style={{ margin: '16px 22px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <div id={headingId} className="field-lbl">Linked incident</div>
        <Link
          to="/backend/alerts"
          search={{}}
          replace
          className="btn btn-sm btn-ghost"
          aria-label="Clear linked incident"
        >
          Clear
        </Link>
      </div>
      {alert ? (
        <>
          <AlertCard a={alert} nameOf={nameOf} onAck={onAck} onResolve={onResolve} busy={busy} />
          {canLogs && !alert.diagnostic && (
            <div style={{ marginTop: 8 }}>
              <Link className="btn btn-sm" to={alertLogsHref(alert)} preload="intent">
                Open related logs
              </Link>
            </div>
          )}
          {query.isError && (
            <QueryErrorNote
              error={query.error}
              what="the latest linked incident state"
              onRetry={() => void query.refetch()}
            />
          )}
        </>
      ) : query.isLoading || query.isPending ? (
        <div className="muted" role="status" aria-live="polite" style={{ padding: 20, textAlign: 'center', fontSize: 12 }}>
          Loading linked incident…
        </div>
      ) : notFound ? (
        <div className="muted" role="status" style={{ padding: 20, textAlign: 'center', fontSize: 12 }}>
          Linked incident not found. It may have been removed or the link is invalid.
        </div>
      ) : query.isError ? (
        <QueryErrorNote error={query.error} what="the linked incident" onRetry={() => void query.refetch()} />
      ) : (
        <div className="muted" role="status" style={{ padding: 20, textAlign: 'center', fontSize: 12 }}>
          Linked incident is unavailable.
        </div>
      )}
      <span className="sr-only">Incident id {incidentId}</span>
    </section>
  );
}

function Kpi({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div className="kpi" style={{ minWidth: 90 }}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-val" style={{ color: color ?? 'var(--ink-1)' }}>{value}</div>
    </div>
  );
}
